import type { Feature } from 'geojson'

import {
  SET_MAP_STYLE,
  TOGGLE_MAP_STYLE,
  TOGGLE_MAP_LAYER,
  TOGGLE_MAP_LAYERS_ON,
  TOGGLE_MAP_LAYERS_OFF,
  MAP_STYLE_SELECTED_FEATURES_SET,
  MAP_STYLE_CLICKED_FEATURE_SET,
  MAP_STYLE_LAYER_FILTER_SET,
  MAP_STYLE_LAYER_FILTERS_SET,
  MAP_STYLE_LAYER_VISIBILITY_SET,
  MAP_STYLE_LAYERS_VISIBILITY_SET,
  MAP_STYLE_LAYERS_FILTER_AND_MODEL_SET,
  MAP_STYLE_LAYER_PAINT_SET,
  MAP_STYLE_LAYER_TYPE_SET,
  MAP_STYLE_SOURCE_DATA_SET,
  MAP_STYLE_SOURCE_SET,
  MAP_STYLE_SOURCES_EMPTY,
  MAP_STYLE_SOURCE_VERSION_BUMP,
  MAP_STYLE_LAYERS_REPLACE,
  MAP_STYLE_BUST_LAYER_TILES,
} from './types'
import type { MapStyleJS } from '@/utilities/mapStyleUtils'

type StyleLayer = Record<string, any>
type LayerVisibility = 'visible' | 'none'
/** A GeoJSON `data` object as carried by a MapLibre GeoJSON source. */
type SourceData = { type: string; features?: Feature[]; [key: string]: unknown }
/** Patch describing the next `clicked_feature` highlight layer + optional source data. */
interface ClickedFeaturePayload {
  layer: StyleLayer
  sourceData?: { sourceId: string; data: SourceData }
}

export type MapStyleAction =
  // SET_MAP_STYLE payload stays `any`: it sets a whole pre-built style (init,
  // base/imagery swap, bulk layer add/remove) whose shape varies across the
  // many callers; tightening it would cascade type errors into those files.
  | { type: typeof SET_MAP_STYLE; payload: any }
  | { type: typeof TOGGLE_MAP_STYLE; payload: string }
  | { type: typeof TOGGLE_MAP_LAYER; payload: string[] }
  | { type: typeof TOGGLE_MAP_LAYERS_ON; payload: string[] }
  | { type: typeof TOGGLE_MAP_LAYERS_OFF; payload: string[] }
  | { type: typeof MAP_STYLE_SELECTED_FEATURES_SET; payload: Feature[] }
  | {
      type: typeof MAP_STYLE_CLICKED_FEATURE_SET
      payload: ClickedFeaturePayload
    }
  | {
      type: typeof MAP_STYLE_LAYER_FILTER_SET
      payload: { layerId: string; filter: unknown }
    }
  | {
      type: typeof MAP_STYLE_LAYER_FILTERS_SET
      payload: Array<{ layerId: string; filter: unknown }>
    }
  | {
      type: typeof MAP_STYLE_LAYER_VISIBILITY_SET
      payload: { layerId: string; visibility: LayerVisibility }
    }
  | {
      type: typeof MAP_STYLE_LAYERS_VISIBILITY_SET
      payload: { layerIds: string[]; visibility: LayerVisibility }
    }
  | {
      type: typeof MAP_STYLE_LAYERS_FILTER_AND_MODEL_SET
      payload: { layerIds: string[]; filter: unknown; filterModel: unknown }
    }
  | {
      type: typeof MAP_STYLE_LAYER_PAINT_SET
      payload: { layerId: string; paint: Record<string, unknown> }
    }
  | {
      type: typeof MAP_STYLE_LAYER_TYPE_SET
      payload: {
        layerId: string
        layerType: string
        paint?: Record<string, unknown>
      }
    }
  | {
      type: typeof MAP_STYLE_SOURCE_DATA_SET
      payload: { sourceId: string; data: SourceData }
    }
  | {
      type: typeof MAP_STYLE_SOURCE_SET
      payload: { sourceId: string; source: unknown }
    }
  | { type: typeof MAP_STYLE_SOURCES_EMPTY; payload: string[] }
  | { type: typeof MAP_STYLE_SOURCE_VERSION_BUMP; payload: { layerId: string } }
  | { type: typeof MAP_STYLE_LAYERS_REPLACE; payload: StyleLayer[] }
  | { type: typeof MAP_STYLE_BUST_LAYER_TILES; payload: { layerId: string } }

// Minimal slice of the store this module's thunks read. The app's root store is
// untyped JS, so we type only what these thunks touch.
interface MapStyleRootState {
  mapStyle: MapStyleJS | null
}
type Dispatch = (action: MapStyleAction) => void
type GetState = () => MapStyleRootState
type MapStyleThunk = (dispatch: Dispatch, getState: GetState) => void

// --- Legacy creators (unchanged) --------------------------------------------

export function setMapStyle(style: any): MapStyleAction {
  return { type: SET_MAP_STYLE, payload: style }
}

export function toggleMapLayer(ids: string[]): MapStyleAction {
  return { type: TOGGLE_MAP_LAYER, payload: ids }
}

export function toggleMapLayersOn(ids: string[]): MapStyleAction {
  return { type: TOGGLE_MAP_LAYERS_ON, payload: ids }
}

export function toggleMapLayersOff(ids: string[]): MapStyleAction {
  return { type: TOGGLE_MAP_LAYERS_OFF, payload: ids }
}

export function toggleMapStyle(style: string): MapStyleAction {
  return { type: TOGGLE_MAP_STYLE, payload: style }
}

// --- Atomic creators (MAM-795) ----------------------------------------------

/** Replace the `selected_features` source features (cyan highlight). */
export function setSelectedFeatures(features: Feature[]): MapStyleAction {
  return { type: MAP_STYLE_SELECTED_FEATURES_SET, payload: features }
}

/** Apply the next `clicked_feature` highlight layer (+ optional source data). */
export function setClickedFeature(
  payload: ClickedFeaturePayload
): MapStyleAction {
  return { type: MAP_STYLE_CLICKED_FEATURE_SET, payload }
}

export function setLayerFilter(
  layerId: string,
  filter: unknown
): MapStyleAction {
  return { type: MAP_STYLE_LAYER_FILTER_SET, payload: { layerId, filter } }
}

/**
 * Set filters on several layers in one atomic pass. A `null` filter on an
 * entry removes that layer's filter (restore-to-baseline). Used by the
 * edit-existing-feature exclusion filter, which composes per-layer values.
 */
export function setLayerFilters(
  entries: Array<{ layerId: string; filter: unknown }>
): MapStyleAction {
  return { type: MAP_STYLE_LAYER_FILTERS_SET, payload: entries }
}

export function setLayerVisibility(
  layerId: string,
  visibility: LayerVisibility
): MapStyleAction {
  return {
    type: MAP_STYLE_LAYER_VISIBILITY_SET,
    payload: { layerId, visibility },
  }
}

/** Set visibility on several layers in one atomic pass. */
export function setLayersVisibility(
  layerIds: string[],
  visibility: LayerVisibility
): MapStyleAction {
  return {
    type: MAP_STYLE_LAYERS_VISIBILITY_SET,
    payload: { layerIds, visibility },
  }
}

/**
 * Apply the same filter + grid `filterModel` to several layers (Data Table
 * "apply filter to map"). A `null` filter removes both the filter and the
 * stored `metadata.filterModel`.
 */
export function setLayersFilterAndModel(
  layerIds: string[],
  filter: unknown,
  filterModel: unknown
): MapStyleAction {
  return {
    type: MAP_STYLE_LAYERS_FILTER_AND_MODEL_SET,
    payload: { layerIds, filter, filterModel },
  }
}

/** Create or replace a whole source object (e.g. LabelFeatures' dynamic-label sources). */
export function setSource(sourceId: string, source: unknown): MapStyleAction {
  return { type: MAP_STYLE_SOURCE_SET, payload: { sourceId, source } }
}

/** Empty several GeoJSON sources (set each `.data` to an empty FeatureCollection). */
export function emptySources(sourceIds: string[]): MapStyleAction {
  return { type: MAP_STYLE_SOURCES_EMPTY, payload: sourceIds }
}

/** Bump the version of the source backing `layerId` to force a tile refetch. */
export function bumpLayerSourceVersion(layerId: string): MapStyleAction {
  return { type: MAP_STYLE_SOURCE_VERSION_BUMP, payload: { layerId } }
}

/** Merge `paint` props into a layer's existing paint object. */
export function setLayerPaint(
  layerId: string,
  paint: Record<string, unknown>
): MapStyleAction {
  return { type: MAP_STYLE_LAYER_PAINT_SET, payload: { layerId, paint } }
}

export function setLayerType(
  layerId: string,
  layerType: string,
  paint?: Record<string, unknown>
): MapStyleAction {
  return {
    type: MAP_STYLE_LAYER_TYPE_SET,
    payload: { layerId, layerType, paint },
  }
}

/** Replace a GeoJSON source's `.data` (e.g. `measure_label_cold`). */
export function setSourceData(
  sourceId: string,
  data: SourceData
): MapStyleAction {
  return { type: MAP_STYLE_SOURCE_DATA_SET, payload: { sourceId, data } }
}

/** Last-resort full `layers` array replace (LabelFeatures rebuild path). */
export function replaceLayers(layers: StyleLayer[]): MapStyleAction {
  return { type: MAP_STYLE_LAYERS_REPLACE, payload: layers }
}

/** Surgically bust the tile cache of the source backing `layerId`. */
export function bustLayerTiles(layerId: string): MapStyleAction {
  return { type: MAP_STYLE_BUST_LAYER_TILES, payload: { layerId } }
}

// --- Thunks (state-dependent; read fresh inside dispatch, no stale closure) --

const getSourceFeatures = (
  state: MapStyleRootState,
  sourceId: string
): Feature[] => {
  const features = state.mapStyle?.sources?.[sourceId]?.data?.features
  return Array.isArray(features) ? features : []
}

/** Append features to `selected_features`, skipping any with a duplicate `mamid`. */
export const appendSelectedFeatures =
  (newFeatures: Feature[]): MapStyleThunk =>
  (dispatch, getState) => {
    const existing = getSourceFeatures(getState(), 'selected_features')
    const existingIds = new Set(
      existing.map(feature => feature.properties?.mamid)
    )
    const deduped = newFeatures.filter(
      feature => !existingIds.has(feature.properties?.mamid)
    )
    if (!deduped.length) return
    dispatch(setSelectedFeatures([...existing, ...deduped]))
  }

/** Remove features from `selected_features` whose `mamid` is in `mamIds`. */
export const removeSelectedFeatures =
  (mamIds: Array<string | number>): MapStyleThunk =>
  (dispatch, getState) => {
    const existing = getSourceFeatures(getState(), 'selected_features')
    if (!existing.length) return
    const removeSet = new Set(mamIds)
    const next = existing.filter(
      feature => !removeSet.has(feature.properties?.mamid)
    )
    if (next.length === existing.length) return
    dispatch(setSelectedFeatures(next))
  }

/**
 * Upsert the label points for a single `measureId` in `measure_label_cold`:
 * drops any existing features tagged with that `measureId`, then appends the
 * new ones. (The measure overlays emit 1–2 points per `measureId`.)
 */
export const upsertMeasureLabels =
  (measureId: string, features: Feature[]): MapStyleThunk =>
  (dispatch, getState) => {
    const existing = getSourceFeatures(getState(), 'measure_label_cold')
    const withoutThis = existing.filter(
      feature => feature.properties?.measureId !== measureId
    )
    const next = [...withoutThis, ...features]
    dispatch(
      setSourceData('measure_label_cold', {
        type: 'FeatureCollection',
        features: next,
      })
    )
  }

/** Remove all `measure_label_cold` features tagged with `measureId`. */
export const removeMeasureLabels =
  (measureId: string): MapStyleThunk =>
  (dispatch, getState) => {
    const existing = getSourceFeatures(getState(), 'measure_label_cold')
    const next = existing.filter(
      feature => feature.properties?.measureId !== measureId
    )
    if (next.length === existing.length) return
    dispatch(
      setSourceData('measure_label_cold', {
        type: 'FeatureCollection',
        features: next,
      })
    )
  }
